// Generic delimited-file parsing + the import-profile registry for
// body_measurement CSV/TSV imports (BODY_MEASUREMENTS_PLAN.md Phase 5,
// D-MEAS-10/11/12/13). RENPHO_TAPE_PROFILE is the first profile; a future
// Withings/Fitbit importer (or a RENPHO smart-scale profile targeting
// vital_sign/body_composition, out of scope here — see plan assumption 2) is
// a new profile object in IMPORT_PROFILES, not new code.
//
// Real RENPHO export observed (Task 5.1, scripts/fixtures/renpho-tape-sample.csv,
// via the RENPHO Health app: Trends -> Circumference -> clock icon -> export):
//   - Comma-delimited, one header row, quoted fields (the timestamp column
//     contains a literal comma so it must be quoted).
//   - WIDE shape: one row per measuring session, one column per site.
//   - Header: "Time of Measurement,Neck(inch),Shoulder(inch),Chest(inch),
//     Waist(inch),Abdomen(inch),Hip(inch),L-Biceps(inch),R-Biceps(inch),
//     L-Thigh(inch),R-Thigh(inch),L-Calf(inch),R-Calf(inch),Waist-Hip Ratio"
//   - Unit is embedded in every site header as "(inch)" -- there is no
//     separate unit column, and every site column in the file shares one
//     unit. stripHeaderUnit() peels this off before the siteAliases lookup.
//   - Timestamp format: `MM/DD/YYYY, HH:MM:SS`, e.g. "03/04/2026, 18:00:32".
//     Not a bare calendar date, so it takes the Date.parse fallback path
//     CsvImportPane.tsx already uses for non-bare-date strings (V8 accepts
//     this exact format and interprets it as local time).
//   - Missing readings are the literal string "--", not an empty cell or 0.
//   - No profile/user column in this export -- it is single-user only. The
//     profile-column plumbing below exists for files that do carry one.
//   - The trailing "Waist-Hip Ratio" (en dash in the raw header) column is
//     RENPHO's own computed ratio; it is ignored on import because
//     bodyMeasurement.ts computes waistToHipRatio() itself from canonical cm.

import type { MeasurementDisplayUnit } from "./bodyMeasurement.js";
import { fromDisplay } from "./bodyMeasurement.js";
import { isCalendarDate, calendarDateToUtcInstant } from "./datetime.js";
import type { EventType } from "./types.js";

// ─── Delimited-file parsing ──────────────────────────────────────────────────
// Moved here from web/src/components/CsvImportPane.tsx (which now imports
// parseDelimited from this module) so the RENPHO/generic importers reuse the
// exact same quoted-field logic rather than a second CSV parser.

function parseDelimitedLine(line: string, delimiter: string): string[] {
  const row: string[] = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuote && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuote = !inQuote;
      }
    } else if (ch === delimiter && !inQuote) {
      row.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  row.push(cur);
  return row;
}

/** Sniffs comma / tab / semicolon by counting (unquoted-agnostic) occurrences
 *  in the header line. Defaults to comma when nothing else appears. */
function sniffDelimiter(headerLine: string): string {
  const candidates: Array<[string, number]> = [
    [",", (headerLine.match(/,/g) ?? []).length],
    ["\t", (headerLine.match(/\t/g) ?? []).length],
    [";", (headerLine.match(/;/g) ?? []).length],
  ];
  candidates.sort((a, b) => b[1] - a[1]);
  const [best, count] = candidates[0]!;
  return count > 0 ? best : ",";
}

/** Parses delimited text into a header row + data rows, sniffing comma / tab /
 *  semicolon and stripping a leading UTF-8 BOM. Handles quoted fields
 *  (including a delimiter or newline-free literal comma inside quotes, e.g.
 *  RENPHO's `"MM/DD/YYYY, HH:MM:SS"` timestamp column). */
export function parseDelimited(text: string): { headers: string[]; rows: string[][]; delimiter: string } {
  const stripped = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const lines = stripped.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { headers: [], rows: [], delimiter: "," };
  const delimiter = sniffDelimiter(lines[0]!);
  const parsed = lines.map((line) => parseDelimitedLine(line, delimiter));
  const [headers, ...rows] = parsed;
  return { headers: headers ?? [], rows, delimiter };
}

const MISSING_VALUE_TOKENS = new Set(["", "--", "-", "n/a", "na", "null", "nan"]);

/** Parses one cell to a number, or null on a missing/placeholder/unparseable
 *  value -- NEVER NaN and NEVER a silent 0 (a dropped row must be visible in
 *  `skipped`, not indistinguishable from a real zero reading).
 *
 *  Decimal-comma normalization only applies when the file's own field
 *  delimiter is NOT a comma -- when it IS, a comma that survived quoted-field
 *  splitting is a thousands separator, not a decimal point, so "34,5" in a
 *  comma-delimited file becomes 345, never a silently-truncated 34. */
export function parseNumber(raw: string, delimiter: string): number | null {
  const trimmed = raw.trim();
  if (MISSING_VALUE_TOKENS.has(trimmed.toLowerCase())) return null;

  let normalized = trimmed;
  if (delimiter === ",") {
    normalized = normalized.replace(/,/g, "");
  } else if (normalized.includes(",")) {
    normalized = normalized.includes(".")
      ? normalized.replace(/,/g, "") // "1,234.5" -> thousands comma, keep the decimal point
      : normalized.replace(",", "."); // "34,5" -> decimal comma
  }

  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

const HEADER_UNIT_RE = /^(.*?)\s*\(\s*(inch|in|cm|centimet(?:er|re))s?\s*\)\s*$/i;

/** Splits a header like "Neck(inch)" into its site label and embedded unit.
 *  Headers without a parenthetical unit suffix (e.g. "Time of Measurement")
 *  come back with `unit: null`. Exported so the import UI can list exactly
 *  the same per-column labels buildMeasurementSessions will resolve, for a
 *  mapping screen that stays in sync with the parser. */
export function stripHeaderUnit(header: string): { label: string; unit: MeasurementDisplayUnit | null } {
  const m = HEADER_UNIT_RE.exec(header.trim());
  if (!m) return { label: header.trim(), unit: null };
  const token = m[2]!.toLowerCase();
  const unit: MeasurementDisplayUnit = token.startsWith("cm") || token.startsWith("centimet") ? "cm" : "in";
  return { label: m[1]!.trim(), unit };
}

/** Lowercase, trim, collapse whitespace, and normalize any dash variant
 *  (RENPHO's header uses an en dash in "Waist-Hip Ratio") to a plain hyphen,
 *  so a single columnRoles/siteAliases key matches every dash spelling.
 *  Exported so the import UI's labelOverrides keys exactly match what
 *  buildMeasurementSessions computes internally. */
export function normalizeHeader(h: string): string {
  return h
    .toLowerCase()
    .trim()
    .replace(/[‐-―]/g, "-")
    .replace(/\s+/g, " ");
}

function parseOccurredAt(raw: string, timezone: string): string | null {
  if (isCalendarDate(raw)) return calendarDateToUtcInstant(raw, timezone);
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString();
}

function parseUnitCell(raw: string | undefined): MeasurementDisplayUnit | null {
  if (!raw) return null;
  const t = raw.trim().toLowerCase();
  if (t === "in" || t === "inch" || t === "inches" || t === '"') return "in";
  if (t === "cm" || t === "centimeter" || t === "centimeters" || t === "centimetre" || t === "centimetres") return "cm";
  return null;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── Import-profile registry ────────────────────────────────────────────────

export type ImportColumnRole = "timestamp" | "site" | "value" | "unit" | "profile" | "ignore";

export interface ImportProfile {
  id: string; // "renpho_tape"
  label: string; // "RENPHO smart tape"
  targetEventType: EventType; // "body_measurement"
  /** "long" = one row per site reading; "wide" = one column per site. */
  shape: "long" | "wide" | "auto";
  /** Lowercased, trimmed header text -> the role it plays. Used for the
   *  timestamp/profile/ignore/value/unit columns; per-site WIDE columns are
   *  matched through `siteAliases` after stripping any embedded unit, not
   *  through this map (their headers vary with the unit chosen at export
   *  time, e.g. "Neck(inch)" vs "Neck(cm)"). */
  columnRoles: Record<string, ImportColumnRole>;
  /** Lowercased, trimmed source body-part label -> our site id. */
  siteAliases: Record<string, string>;
  /** Unit when the file carries none of its own (no embedded header unit,
   *  no unit column, and the caller supplied no fallbackUnit either).
   *  undefined = the user must choose. */
  assumedUnit?: MeasurementDisplayUnit;
  /** 0..1 confidence that this profile matches the given headers. */
  detect(headers: string[]): number;
}

/** Body-part label -> catalog site id. Shared by RENPHO's wide hyphenated
 *  headers ("L-Biceps") and the generic long-shape fallback's word-based
 *  labels ("left bicep") -- both are just spellings of the same sites
 *  (BODY_MEASUREMENTS_PLAN.md §6). A bare, side-less label ("arm") maps to
 *  the LEFT site; the import UI (Task 5.4) must show this as a guess the
 *  user can override, not a silent choice. */
const SITE_LABEL_ALIASES: Record<string, string> = {
  neck: "neck",
  shoulder: "shoulders",
  shoulders: "shoulders",
  chest: "chest",
  bust: "chest",
  "upper chest": "chest",
  waist: "waist_narrow",
  "natural waist": "waist_narrow",
  abdomen: "abdomen",
  abs: "abdomen",
  belly: "abdomen",
  stomach: "abdomen",
  hip: "hips",
  hips: "hips",

  "l-biceps": "bicep_relaxed_l",
  "r-biceps": "bicep_relaxed_r",
  "left arm": "bicep_relaxed_l",
  "left bicep": "bicep_relaxed_l",
  "left upper arm": "bicep_relaxed_l",
  "arm (left)": "bicep_relaxed_l",
  "right arm": "bicep_relaxed_r",
  "right bicep": "bicep_relaxed_r",
  "right upper arm": "bicep_relaxed_r",
  "arm (right)": "bicep_relaxed_r",
  arm: "bicep_relaxed_l",

  "l-forearm": "forearm_l",
  "r-forearm": "forearm_r",
  "left forearm": "forearm_l",
  "forearm (left)": "forearm_l",
  "right forearm": "forearm_r",
  "forearm (right)": "forearm_r",

  "l-wrist": "wrist_l",
  "r-wrist": "wrist_r",
  "left wrist": "wrist_l",
  "right wrist": "wrist_r",

  "l-thigh": "thigh_mid_l",
  "r-thigh": "thigh_mid_r",
  "left thigh": "thigh_mid_l",
  "thigh (left)": "thigh_mid_l",
  "right thigh": "thigh_mid_r",
  "thigh (right)": "thigh_mid_r",
  thigh: "thigh_mid_l",

  "l-calf": "calf_l",
  "r-calf": "calf_r",
  "left calf": "calf_l",
  "calf (left)": "calf_l",
  "right calf": "calf_r",
  "calf (right)": "calf_r",
  calf: "calf_l",

  "l-ankle": "ankle_l",
  "r-ankle": "ankle_r",
  "left ankle": "ankle_l",
  "right ankle": "ankle_r",
};

/** The RENPHO smart-tape profile -- WIDE shape, unit embedded per header,
 *  "--" for a site that wasn't measured that session (D-MEAS-11). */
export const RENPHO_TAPE_PROFILE: ImportProfile = {
  id: "renpho_tape",
  label: "RENPHO smart tape",
  targetEventType: "body_measurement",
  shape: "wide",
  columnRoles: {
    "time of measurement": "timestamp",
    "waist-hip ratio": "ignore",
    "waist to hip ratio": "ignore",
  },
  siteAliases: SITE_LABEL_ALIASES,
  detect(headers: string[]): number {
    const stripped = headers.map((h) => normalizeHeader(stripHeaderUnit(h).label));
    const hasTimestamp = stripped.includes("time of measurement");
    if (!hasTimestamp) return 0;
    const siteMatches = stripped.filter((h) => SITE_LABEL_ALIASES[h] !== undefined).length;
    return Math.min(1, 0.5 + siteMatches * 0.05);
  },
};

const GENERIC_TIMESTAMP_HEADERS = new Set(["date", "time", "timestamp", "datetime", "time of measurement"]);
const GENERIC_SITE_HEADERS = new Set(["site", "body part", "bodypart", "location", "measurement", "part"]);
const GENERIC_VALUE_HEADERS = new Set(["value", "measurement value", "reading", "amount"]);
const GENERIC_UNIT_HEADERS = new Set(["unit", "units"]);
const GENERIC_PROFILE_HEADERS = new Set(["profile", "user", "name", "profile name", "user name"]);

/** Fallback for any long-shape (one row per site reading) export that isn't
 *  RENPHO's. A future Withings/Fitbit CSV importer is a new profile object,
 *  not a change to this one (D-MEAS-11). */
export const GENERIC_LONG_PROFILE: ImportProfile = {
  id: "generic_long",
  label: "Generic (one row per measurement)",
  targetEventType: "body_measurement",
  shape: "long",
  columnRoles: Object.fromEntries([
    ...[...GENERIC_TIMESTAMP_HEADERS].map((h) => [h, "timestamp" as const]),
    ...[...GENERIC_SITE_HEADERS].map((h) => [h, "site" as const]),
    ...[...GENERIC_VALUE_HEADERS].map((h) => [h, "value" as const]),
    ...[...GENERIC_UNIT_HEADERS].map((h) => [h, "unit" as const]),
    ...[...GENERIC_PROFILE_HEADERS].map((h) => [h, "profile" as const]),
  ]),
  siteAliases: SITE_LABEL_ALIASES,
  detect(headers: string[]): number {
    const norm = headers.map((h) => normalizeHeader(h));
    const hasTimestamp = norm.some((h) => GENERIC_TIMESTAMP_HEADERS.has(h));
    const hasSite = norm.some((h) => GENERIC_SITE_HEADERS.has(h));
    const hasValue = norm.some((h) => GENERIC_VALUE_HEADERS.has(h));
    return hasTimestamp && hasSite && hasValue ? 0.5 : 0.05;
  },
};

export const IMPORT_PROFILES: readonly ImportProfile[] = [RENPHO_TAPE_PROFILE, GENERIC_LONG_PROFILE];

/** Picks the best-matching profile for a file's headers. Always returns a
 *  profile (GENERIC_LONG_PROFILE's floor confidence keeps this total) so the
 *  caller can offer an override dropdown rather than a dead end. */
export function pickImportProfile(headers: string[]): { profile: ImportProfile; confidence: number } {
  let best = IMPORT_PROFILES[0]!;
  let bestScore = -1;
  for (const profile of IMPORT_PROFILES) {
    const score = profile.detect(headers);
    if (score > bestScore) {
      bestScore = score;
      best = profile;
    }
  }
  return { profile: best, confidence: bestScore };
}

/** Resolves an "auto" profile's shape for a given header set: wide when 3 or
 *  more headers resolve through siteAliases (after stripping any embedded
 *  unit), long otherwise. RENPHO_TAPE_PROFILE and GENERIC_LONG_PROFILE both
 *  declare a fixed shape already, so this only matters for a future
 *  shape-agnostic profile. */
export function detectShape(headers: string[], profile: ImportProfile): "long" | "wide" {
  if (profile.shape !== "auto") return profile.shape;
  const stripped = headers.map((h) => normalizeHeader(stripHeaderUnit(h).label));
  const matches = stripped.filter((h) => profile.siteAliases[h] !== undefined).length;
  return matches >= 3 ? "wide" : "long";
}

// ─── Building sessions from parsed rows ──────────────────────────────────────

export interface ImportSession {
  occurredAt: string;
  sites: Record<string, { value: number; unit: MeasurementDisplayUnit }>;
  profileName?: string;
}

export interface BuildMeasurementSessionsArgs {
  headers: string[];
  rows: string[][];
  delimiter: string;
  profile: ImportProfile;
  /** Local timezone for a bare-calendar-date timestamp column, matching
   *  CsvImportPane.tsx's noon-anchoring fix (D7). Full datetime strings
   *  (RENPHO's format) ignore this and parse as-is. */
  timezone: string;
  /** Unit for a column/row that carries no unit of its own. Required unless
   *  every relevant column embeds its own unit (as RENPHO's wide headers do)
   *  or `profile.assumedUnit` covers it -- throws otherwise so a value is
   *  never silently mis-scaled (D-MEAS-11). */
  fallbackUnit?: MeasurementDisplayUnit;
  /** User overrides from the mapping screen for headers/labels that didn't
   *  resolve through `profile.siteAliases`: normalized label -> site id, or
   *  "skip" to explicitly drop it. */
  labelOverrides?: Record<string, string | "skip">;
}

export interface BuildMeasurementSessionsResult {
  sessions: ImportSession[];
  unmatchedLabels: string[];
  skipped: { row: number; reason: string }[];
}

function resolveSiteId(
  normalizedLabel: string,
  profile: ImportProfile,
  labelOverrides: Record<string, string | "skip"> | undefined,
): string | null | "skip" {
  const override = labelOverrides?.[normalizedLabel];
  if (override === "skip") return "skip";
  if (override) return override;
  return profile.siteAliases[normalizedLabel] ?? null;
}

function buildWideSessions(args: BuildMeasurementSessionsArgs): BuildMeasurementSessionsResult {
  const { headers, rows, delimiter, profile, fallbackUnit, labelOverrides } = args;
  const unmatchedLabels = new Set<string>();
  const skipped: { row: number; reason: string }[] = [];

  let timestampIdx: number | null = null;
  let profileIdx: number | null = null;
  const siteCols: { index: number; siteId: string; unit: MeasurementDisplayUnit | null; label: string }[] = [];

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    const role = profile.columnRoles[normalized];
    if (role === "timestamp") {
      timestampIdx = index;
      return;
    }
    if (role === "profile") {
      profileIdx = index;
      return;
    }
    if (role === "ignore") return;

    const { label, unit } = stripHeaderUnit(header);
    const normLabel = normalizeHeader(label);
    const resolved = resolveSiteId(normLabel, profile, labelOverrides);
    if (resolved === "skip") return;
    if (resolved === null) {
      unmatchedLabels.add(normLabel);
      return;
    }
    siteCols.push({ index, siteId: resolved, unit, label: normLabel });
  });

  if (timestampIdx === null) {
    throw new Error("No timestamp column recognized. Map one on the import screen.");
  }
  const tsIdx: number = timestampIdx;
  const profIdx: number | null = profileIdx;

  const sessions: ImportSession[] = [];
  rows.forEach((row, rowIdx) => {
    const rawTs = (row[tsIdx] ?? "").trim();
    if (!rawTs) {
      skipped.push({ row: rowIdx, reason: "missing timestamp" });
      return;
    }
    const occurredAt = parseOccurredAt(rawTs, args.timezone);
    if (!occurredAt) {
      skipped.push({ row: rowIdx, reason: `unparseable timestamp "${rawTs}"` });
      return;
    }

    const sites: ImportSession["sites"] = {};
    for (const col of siteCols) {
      const raw = row[col.index] ?? "";
      const value = parseNumber(raw, delimiter);
      if (value === null) continue; // "--" / blank: this site just wasn't measured this session
      const unit = col.unit ?? fallbackUnit ?? profile.assumedUnit;
      if (!unit) throw new Error(`No unit known for column "${col.label}" -- pass fallbackUnit.`);
      sites[col.siteId] = { value, unit };
    }
    if (Object.keys(sites).length === 0) {
      skipped.push({ row: rowIdx, reason: "no measurements in row" });
      return;
    }
    const profileName = profIdx !== null ? (row[profIdx] ?? "").trim() || undefined : undefined;
    sessions.push({ occurredAt, sites, profileName });
  });

  return { sessions, unmatchedLabels: [...unmatchedLabels], skipped };
}

function buildLongSessions(args: BuildMeasurementSessionsArgs): BuildMeasurementSessionsResult {
  const { headers, rows, delimiter, profile, fallbackUnit, labelOverrides } = args;
  const unmatchedLabels = new Set<string>();
  const skipped: { row: number; reason: string }[] = [];

  let timestampIdx: number | null = null;
  let siteIdx: number | null = null;
  let valueIdx: number | null = null;
  let unitIdx: number | null = null;
  let profileIdx: number | null = null;

  headers.forEach((header, index) => {
    const role = profile.columnRoles[normalizeHeader(header)];
    if (role === "timestamp" && timestampIdx === null) timestampIdx = index;
    else if (role === "site" && siteIdx === null) siteIdx = index;
    else if (role === "value" && valueIdx === null) valueIdx = index;
    else if (role === "unit" && unitIdx === null) unitIdx = index;
    else if (role === "profile" && profileIdx === null) profileIdx = index;
  });

  if (timestampIdx === null || siteIdx === null || valueIdx === null) {
    throw new Error("Long-shape import requires timestamp, site, and value columns to be mapped.");
  }
  const tsIdx: number = timestampIdx;
  const siteColIdx: number = siteIdx;
  const valColIdx: number = valueIdx;
  const unitColIdx: number | null = unitIdx;
  const profColIdx: number | null = profileIdx;

  const groups = new Map<string, ImportSession>();
  rows.forEach((row, rowIdx) => {
    const rawTs = (row[tsIdx] ?? "").trim();
    if (!rawTs) {
      skipped.push({ row: rowIdx, reason: "missing timestamp" });
      return;
    }
    const occurredAt = parseOccurredAt(rawTs, args.timezone);
    if (!occurredAt) {
      skipped.push({ row: rowIdx, reason: `unparseable timestamp "${rawTs}"` });
      return;
    }

    const rawLabel = row[siteColIdx] ?? "";
    const normLabel = normalizeHeader(rawLabel);
    const resolved = resolveSiteId(normLabel, profile, labelOverrides);
    if (resolved === "skip") return;
    if (resolved === null) {
      unmatchedLabels.add(normLabel);
      skipped.push({ row: rowIdx, reason: `unmapped site label "${rawLabel}"` });
      return;
    }

    const rawValue = row[valColIdx] ?? "";
    const value = parseNumber(rawValue, delimiter);
    if (value === null) {
      skipped.push({ row: rowIdx, reason: `unparseable value "${rawValue}"` });
      return;
    }
    const unit = (unitColIdx !== null ? parseUnitCell(row[unitColIdx]) : null) ?? fallbackUnit ?? profile.assumedUnit;
    if (!unit) throw new Error(`No unit known for row ${rowIdx + 1} -- pass fallbackUnit.`);

    const profileName = profColIdx !== null ? (row[profColIdx] ?? "").trim() || undefined : undefined;
    const groupKey = `${occurredAt}|${profileName ?? ""}`;
    let group = groups.get(groupKey);
    if (!group) {
      group = { occurredAt, sites: {}, profileName };
      groups.set(groupKey, group);
    }
    group.sites[resolved] = { value, unit };
  });

  const sessions = [...groups.values()].filter((g) => Object.keys(g.sites).length > 0);
  return { sessions, unmatchedLabels: [...unmatchedLabels], skipped };
}

/** Turns parsed rows into sessions, per D-MEAS-12: every timestamped reading
 *  is kept as its own session, nothing averaged. WIDE rows are already one
 *  session each; LONG rows are grouped by exact (timestamp, profile) pair. */
export function buildMeasurementSessions(args: BuildMeasurementSessionsArgs): BuildMeasurementSessionsResult {
  const shape = detectShape(args.headers, args.profile);
  return shape === "wide" ? buildWideSessions(args) : buildLongSessions(args);
}

// ─── Dedup external id ───────────────────────────────────────────────────────

/** Stable dedup key for one imported session, per BODY_MEASUREMENTS_PLAN.md
 *  §6 -- hashed against CANONICAL cm values (via fromDisplay), never the raw
 *  display value, and rounded to 2dp before hashing so float noise in the
 *  15th decimal place never produces a spurious duplicate on re-import. */
export async function measurementImportExternalId(
  patientId: string,
  profileId: string,
  session: ImportSession,
): Promise<string> {
  const canonicalSites = Object.entries(session.sites)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([id, v]) => `${id}=${fromDisplay(v.value, v.unit).toFixed(2)}`)
    .join(",");
  const raw = `${patientId}|body_measurement|${profileId}|${session.occurredAt}|${canonicalSites}`;
  return (await sha256Hex(raw)).slice(0, 24);
}
